import React, { useState, useEffect } from "react";
import {
    Users,
    Video,
    Activity,
    CreditCard,
    TrendingUp,
    Loader2,
    Database,
    ShieldCheck
} from "lucide-react";
import { adminService, AdminStats } from "../../services/admin.service";
import { Sidebar, Header, ToastContainer } from "../Dashboard/DashboardComponents";
import { authService } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const AdminDashboard = () => {
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const loadData = async () => {
            try {
                const [userData, statsData] = await Promise.all([
                    authService.getProfile(),
                    adminService.getStats()
                ]);

                if (userData.role !== 'admin') {
                    navigate("/dashboard");
                    return;
                }

                setUser(userData);
                setStats(statsData);
            } catch (err) {
                console.error("Admin dashboard load error:", err);
                navigate("/dashboard");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [navigate]);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
                    <span className="text-sm text-slate-500 font-medium">Loading statistics...</span>
                </div>
            </div>
        );
    }

    const statCards = [
        {
            label: "Total Users",
            value: stats?.totalUsers || 0,
            icon: Users,
            color: "text-blue-600",
            bg: "bg-blue-50",
            trend: "+12.4%",
            isPositive: true,
            onClick: () => navigate("/admin/users")
        },
        {
            label: "Active Projects",
            value: stats?.totalProjects || 0,
            icon: Video,
            color: "text-purple-600",
            bg: "bg-purple-50",
            trend: "+5.1%",
            isPositive: true
        },
        {
            label: "Videos Exported",
            value: stats?.totalExports || 0,
            icon: Activity,
            color: "text-emerald-600",
            bg: "bg-emerald-50",
            trend: "+31.2%",
            isPositive: true
        },
        {
            label: "Active Subscriptions",
            value: stats?.activeSubscriptions || 0,
            icon: CreditCard,
            color: "text-amber-600",
            bg: "bg-amber-50",
            trend: "-1.8%",
            isPositive: false
        }
    ];

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 font-sans">
            <Sidebar
                isOpen={sidebarOpen}
                isMobile={isMobile}
                user={user}
                onToggle={toggleSidebar}
                activePage="admin"
            />

            <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={toggleSidebar}
                    user={user}
                    onLogout={() => { authService.logout(); navigate("/login"); }}
                    title="Admin Hub"
                />

                <main className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
                    <div className="max-w-7xl mx-auto space-y-10">

                        {/* Title Section */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                                    Admin Overview
                                </h1>
                                <p className="text-slate-500 text-sm mt-1">Platform-wide statistics and management.</p>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm"></div>
                                <span className="text-xs font-semibold text-emerald-700">All systems online</span>
                            </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {statCards.map((stat, i) => (
                                <motion.div
                                    key={i}
                                    onClick={stat.onClick}
                                    style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="bg-white border border-slate-200 p-6 rounded-2xl hover:border-indigo-200 hover:shadow-lg transition-all shadow-sm flex flex-col"
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <div className={`w-12 h-12 ${stat.bg} ${stat.color} rounded-xl flex items-center justify-center`}>
                                            <stat.icon size={24} />
                                        </div>
                                        <div className={`flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full ${stat.isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                            {stat.trend}
                                            <TrendingUp size={12} className={stat.isPositive ? '' : 'rotate-180'} />
                                        </div>
                                    </div>
                                    <p className="text-xs font-semibold text-slate-500 mb-1">{stat.label}</p>
                                    <h3 className="text-3xl font-bold text-slate-900 tabular-nums">{stat.value.toLocaleString()}</h3>
                                </motion.div>
                            ))}
                        </div>

                        {/* Main Panels */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2">
                                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                <Database size={18} />
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-900">Recent Activity</h3>
                                        </div>
                                        <button className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors">See all</button>
                                    </div>

                                    <div className="space-y-3">
                                        {[1, 2, 3, 4, 5].map((_, i) => (
                                            <div key={i} className="flex items-center gap-4 p-4 hover:bg-slate-50 rounded-xl transition-colors">
                                                <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-slate-900">User Login</p>
                                                    <p className="text-xs text-slate-500">Subject logged in as user_{i + 800}@vidlaya.com</p>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-[11px] font-bold text-slate-400">{i * 12}m ago</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="bg-indigo-600 rounded-3xl p-8 shadow-lg shadow-indigo-100 flex flex-col h-full">
                                    <h3 className="text-xl font-bold text-white mb-2">Service Status</h3>
                                    <p className="text-indigo-100 text-xs font-medium mb-8 leading-relaxed">Platform infrastructure is operating within normal parameters.</p>

                                    <div className="space-y-3 mt-auto">
                                        {[
                                            { label: "API Status", status: "Online", color: "bg-emerald-400" },
                                            { label: "Database", status: "Stable", color: "bg-emerald-400" },
                                            { label: "Export Service", status: "Active", color: "bg-emerald-400" }
                                        ].map((svc, i) => (
                                            <div key={i} className="flex items-center justify-between bg-white/10 p-4 rounded-xl border border-white/5">
                                                <span className="text-xs font-bold text-white">{svc.label}</span>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${svc.color}`}></div>
                                                    <span className="text-[10px] font-bold text-white uppercase tracking-wider">{svc.status}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                            <ShieldCheck size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-900">Admin Access</p>
                                            <p className="text-[11px] text-slate-500 font-medium">Verified Identity</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default AdminDashboard;
